import { Injectable } from '@angular/core';
import { LayoutService } from '../../layout/services/layout.service';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { IChangePassword, IUser, IUserDeleteResponse } from '../interfaces/user.interface';
import { IResponse } from '../../shared/interface/api.interface';
import { IListOfValue } from '../../shared/interface/list-of-value.interface';
import { IAppState } from '../../store/interface/app.state.interface';
import { Store } from '@ngrx/store';
import * as UserActions from '../../store/user/user.actions';
import { buildHttpParamsForList, IPageProps } from '../../shared/utils/table';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private apiUrl = '/users';
    private getUserTypeUrl = '/users/types';
    private getUserRoleUrl = '/roles';

    constructor(
        private http: HttpClient,
        private store: Store<IAppState>,
        private layoutService: LayoutService,
    ) {}

    async getUsers(params?: IPageProps): Promise<IResponse<IUser[]>> {
        const httpParams = buildHttpParamsForList(params);
        return firstValueFrom(
            this.http.get<IResponse<IUser[]>>(this.apiUrl, { params: httpParams }),
        );
    }

    async getTenantCustomers(params?: IPageProps): Promise<IResponse<IUser[]>> {
        const httpParams = buildHttpParamsForList(params);
        return firstValueFrom(
            this.http.get<IResponse<IUser[]>>(`${this.apiUrl}/all-customers-by-tenant`, {
                params: httpParams,
            }),
        );
    }

    async getUserTypes(): Promise<IResponse<IListOfValue[]>> {
        return firstValueFrom(this.http.get<IResponse<IListOfValue[]>>(this.getUserTypeUrl));
    }

    async getUserRoles(searchText = '', tenantId?: string | null): Promise<IListOfValue[]> {
        return firstValueFrom(
            this.http.get<IResponse<IListOfValue[]>>(`${this.getUserRoleUrl}/list-of-values`, {
                params: { searchText, ...(tenantId ? { tenantId } : {}) },
            }),
        ).then((res) => res.data);
    }

    async getUserById(id: string): Promise<IUser> {
        return firstValueFrom(this.http.get<IResponse<IUser>>(`${this.apiUrl}/${id}`)).then(
            (res) => res.data,
        );
    }

    async createUser(user: IUser): Promise<IUser> {
        return firstValueFrom(this.http.post<IResponse<IUser>>(this.apiUrl, user)).then(
            (res) => res.data,
        );
    }

    async updateUser(userId: string, user: IUser): Promise<IUser> {
        return firstValueFrom(
            this.http.patch<IResponse<IUser>>(`${this.apiUrl}/${userId}`, user),
        ).then((res) => res.data);
    }

    async deleteUser(id: string): Promise<IUserDeleteResponse> {
        return firstValueFrom(
            this.http.delete<IResponse<IUserDeleteResponse>>(`${this.apiUrl}/${id}`),
        ).then((res) => res.data);
    }

    async restoreUser(id: string): Promise<IUserDeleteResponse> {
        return firstValueFrom(
            this.http.put<IResponse<IUserDeleteResponse>>(`${this.apiUrl}/${id}/restore`, {}),
        ).then((res) => res.data);
    }

    async fetchLoggedInUser(): Promise<void> {
        return firstValueFrom(this.http.get<IResponse<IUser>>('/users/me')).then((res) => {
            this.store.dispatch(UserActions.setLoggedInUser({ user: res.data }));
            this.layoutService.applyTenantLayoutTheme(res.data?.tenant);
        });
    }

    async updateUserProfile(id: string, user: IUser): Promise<IUser> {
        return firstValueFrom(
            this.http.put<IResponse<IUser>>(`${this.apiUrl}/${id}/profile`, user),
        ).then((res) => res.data);
    }

    async changePassword(changePassword: IChangePassword): Promise<IChangePassword> {
        return firstValueFrom(
            this.http.put<IResponse<IChangePassword>>(
                `${this.apiUrl}/update-password`,
                changePassword,
            ),
        ).then((res) => res.data);
    }
}
